import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import apiService from "../../services/api.js";
import { Clock, ChevronRight } from "lucide-react";

// --- Constants ---
const DEFAULT_LINES = ["待生成", "待生成", "待生成", "待生成", "待生成", "待生成"];
const LINE_NAMES = ["上爻", "五爻", "四爻", "三爻", "二爻", "初爻"];
const METHOD_OPTIONS = [
  { value: "zhuyao", label: "在线摇卦" },
  { value: "baoshu", label: "报数成卦" },
  { value: "zhiding", label: "指定成卦" },
];

const ZHIDING_OPTIONS = [
  { value: "000", label: "老阴— —(动)" },
  { value: "100", label: "少阳——— " },
  { value: "110", label: "少阴— — " },
  { value: "111", label: "老阳———(动)" },
];

const ZHIDING_MAP = {
  "000": "老阴",
  "100": "少阳",
  "110": "少阴",
  "111": "老阳"
};

const COIN_HAN = "/images/qianlong_han.png";
const COIN_MAN = "/images/qianlong_man.png";

// --- Sub-components ---

const BasicInfoForm = ({ formData, handleChange, zhantiOptions, errors, resetToCurrentTime }) => (
  <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full">
    <div className="space-y-3">
      {/* Row 1: Qigua Time */}
      <div className="bg-indigo-50/50 p-3 rounded-xl border border-indigo-100">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-bold text-indigo-900">
            起卦时间 <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={resetToCurrentTime}
            className="h-6 text-[10px] bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 font-medium px-2 rounded-md flex items-center gap-1 transition-all shadow-sm whitespace-nowrap"
          >
            <Clock className="h-3 w-3" />
            回到当前时间
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { key: "year", unit: "年" },
            { key: "month", unit: "月" },
            { key: "day", unit: "日" },
            { key: "hour", unit: "时" }
          ].map((field) => (
            <div key={field.key} className="relative min-w-0">
              <input
                type="number"
                name={field.key}
                value={formData[field.key]}
                onChange={handleChange}
                className="block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 pr-5 text-center font-medium text-gray-700"
              />
              <span className="absolute right-2 top-2 text-gray-400 text-xs pointer-events-none font-light">
                {field.unit}
              </span>
            </div>
          ))}
        </div>
        {errors.year && <div className="text-red-600 text-xs mt-2">{errors.year}</div>}
      </div>

      {/* Row 2: Type & Content */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-4 relative">
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className={`block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 bg-white transition-colors ${formData.type ? "text-gray-700" : "text-gray-400"}`}
          >
            <option value="">占类（必填）</option>
            {zhantiOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
         
          {errors.type && <div className="text-red-600 text-xs mt-1 flex items-center"><span className="mr-1">!</span>{errors.type}</div>}
        </div>
        <div className="col-span-8 relative">
          <input
            type="text"
            name="content"
            value={formData.content}
            onChange={handleChange}
            maxLength={50}
            className="block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 pr-6 transition-colors"
            placeholder="求占问题（必填）50字以内"
          />
           {errors.content && <div className="text-red-600 text-xs mt-1 flex items-center"><span className="mr-1">!</span>{errors.content}</div>}
        </div>
      </div>

      {/* Row 3: Name, Gender, Birth Year */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-4">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 transition-colors"
            placeholder="姓名"
            maxLength={10}
          />
        </div>
        <div className="col-span-4">
          <div className="relative">
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className={`block w-full h-10 rounded-lg border border-gray-300 bg-gradient-to-b from-white to-gray-50 px-3 pr-8 text-base sm:text-sm shadow-sm appearance-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${formData.gender ? "text-gray-700" : "text-gray-400"}`}
            >
              <option value="">性别（婚姻）</option>
              <option value="男">男</option>
              <option value="女">女</option>
            </select>
            <ChevronRight className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-gray-400" />
          </div>
        </div>
        <div className="col-span-4">
          <div className="relative group">
            <input
              type="number"
              name="birth_year"
              value={formData.birth_year || ""}
              onChange={handleChange}
              placeholder="生年YYYY"
              className="block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 text-center font-medium text-gray-700 group-hover:border-indigo-300 transition-colors"
            />
            
          </div>
        </div>
      </div>
    </div>
  </section>
);

const DivinationMethod = ({
  formData,
  handleMethodChange,
  lines,
  setLines,
  baoshuInputs,
  currentBaoshuInput,
  handleCurrentBaoshuChange,
  confirmBaoshuInput,
  zhidingInputs,
  currentZhidingInput,
  handleZhidingSelect,
  handleStart,
  resetAll,
  errors,
}) => {
  // Helper to find the next empty slot index for Baoshu (0 to 5, representing 初爻 to 上爻)
  const filledCount = baoshuInputs.filter(v => v !== "").length;
  const isBaoshuComplete = filledCount === 6;

  const filledZhidingCount = zhidingInputs.filter(v => v !== "").length;
  const isZhidingComplete = filledZhidingCount === 6;

  // Zhuyao Logic
  const filledZhuyaoCount = lines.filter(v => v !== "待生成").length;
  const isZhuyaoComplete = filledZhuyaoCount === 6;
  const [isShaking, setIsShaking] = useState(false);
  const [currentCoins, setCurrentCoins] = useState([0, 0, 0]); // 0: Han, 1: Man
  const shakeInterval = useRef(null);

  useEffect(() => {
    return () => {
      if (shakeInterval.current) clearInterval(shakeInterval.current);
    };
  }, []);

  const handleCoinClick = () => {
    if (isZhuyaoComplete || isShaking) return;

    // Start shaking
    setIsShaking(true);
    shakeInterval.current = setInterval(() => {
      setCurrentCoins([
        Math.random() > 0.5 ? 1 : 0,
        Math.random() > 0.5 ? 1 : 0,
        Math.random() > 0.5 ? 1 : 0
      ]);
    }, 80);

    // Auto stop after 2 seconds
    setTimeout(() => {
      if (shakeInterval.current) clearInterval(shakeInterval.current);
      setIsShaking(false);

      const finalCoins = [
        Math.random() > 0.5 ? 1 : 0,
        Math.random() > 0.5 ? 1 : 0,
        Math.random() > 0.5 ? 1 : 0
      ];
      setCurrentCoins(finalCoins);

      // Record result
      // Man is 1, Han is 0.
      // We store a string like "101" (2 Man, 1 Han)
      const val = finalCoins.join("");
      const targetIndex = 5 - filledZhuyaoCount;
      setLines(prev => {
        const newLines = [...prev];
        newLines[targetIndex] = val;
        return newLines;
      });
    }, 1000);
  };

  const getManCount = (val) => {
    if (val === "待生成") return null;
    const digits = String(val).replace(/\D/g, "");
    let count = 0;
    for (let ch of digits) {
      if (ch === '1') count++;
    }
    return count;
  };

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
      <div className="border-b border-gray-100 pb-2 mb-3 flex items-center gap-3">
        <h3 className="text-lg font-bold text-gray-900 flex items-center whitespace-nowrap">
          <span className="w-1 h-6 bg-green-500 rounded-full mr-3"></span>
          起卦
        </h3>
        <div className="relative flex-1">
          <select
            name="method"
            value={formData.method}
            onChange={handleMethodChange}
            className="block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 bg-white appearance-none cursor-pointer hover:border-indigo-300 transition-colors"
          >
            <option value="" disabled>起卦方式</option>
            {METHOD_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
            <ChevronRight className="h-4 w-4 rotate-90" />
          </div>
        </div>
      </div>

      <div className="flex-grow">
        {!formData.method && (
          <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
            <p className="text-gray-400">心念占事，点击投掷</p>
            <p className="text-gray-400">每投必缓，六掷卦成</p>
          </div>
        )}

        {formData.method === "zhuyao" && (
          <>
            <div className="bg-blue-50 text-blue-700 text-xs p-2 rounded-lg mb-3 flex items-start">
              点击铜钱开始摇卦，1秒后自动停止。
            </div>
            
            {!isZhuyaoComplete && (
              <div className="flex flex-col items-center mb-4">
                <div 
                  onClick={handleCoinClick}
                  className="flex gap-3 p-4 bg-white rounded-2xl cursor-pointer hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm"
                >
                  {currentCoins.map((coin, idx) => (
                    <img 
                      key={idx} 
                      src={coin === 0 ? COIN_HAN : COIN_MAN} 
                      alt={coin === 0 ? "Han" : "Man"}
                      className={`w-14 h-14 object-contain transition-transform duration-100 ${isShaking ? 'scale-110' : ''}`}
                    />
                  ))}
                </div>
                <div className="mt-2 text-[10px] text-gray-400">
                  {isShaking ? "正在摇卦中..." : "点击图片开始摇卦"}
                </div>
              </div>
            )}

            <div className="mt-2 p-2 bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-xs text-gray-500 mb-2">已生成卦爻（从初爻到上爻）：</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {[5, 4, 3, 2, 1, 0].map((idx, i) => {
                  const val = lines[idx];
                  const manCount = getManCount(val);
                  return (
                    <div key={idx} className="flex flex-col items-center">
                      <div className={`w-12 h-9 flex items-center justify-center rounded border font-medium text-xs transition-all ${
                        val !== "待生成"
                          ? 'bg-white border-indigo-500 text-indigo-600 shadow-sm' 
                          : 'bg-gray-100 border-gray-200 text-gray-300 border-dashed'
                      }`}>
                        {val !== "待生成" ? `${manCount}个背` : "?"}
                      </div>
                      <span className="text-[10px] text-gray-300">{LINE_NAMES[idx]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {formData.method === "baoshu" && (
          <>
            <div className="bg-blue-50 text-blue-700 text-xs p-2 rounded-lg mb-3 leading-tight space-y-0.5">
              请输入任意三位数，共六组
            </div>
            
            {!isBaoshuComplete && (
              <div className="flex gap-2 mb-4 items-end">
                <div className="w-1/3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    请输入第 {filledCount + 1} 组数字 
                  </label>
                  <input
                    value={currentBaoshuInput}
                    onChange={handleCurrentBaoshuChange}
                    onKeyDown={(e) => e.key === 'Enter' && confirmBaoshuInput()}
                    placeholder="000"
                    inputMode="numeric"
                    pattern="\d{3}"
                    maxLength={3}
                    className="block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 text-center font-mono text-lg"
                    autoFocus
                  />
                </div>
                <button
                  onClick={confirmBaoshuInput}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium shadow-sm transition-colors h-[38px] text-sm"
                >
                  确认
                </button>
              </div>
            )}

            <div className="mt-2 p-2 bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-xs text-gray-500 mb-2">已录入数字（从初爻到上爻）：</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {[5, 4, 3, 2, 1, 0].map((idx, i) => (
                  <div key={idx} className="flex flex-col items-center">
                    <div className={`w-12 h-9 flex items-center justify-center rounded border font-mono text-sm font-bold transition-all ${
                      baoshuInputs[idx] 
                        ? 'bg-white border-indigo-500 text-indigo-600 shadow-sm' 
                        : 'bg-gray-100 border-gray-200 text-gray-300 border-dashed'
                    }`}>
                      {baoshuInputs[idx] || "?"}
                    </div>
                    <span className="text-[10px] text-gray-300">{LINE_NAMES[idx]}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {formData.method === "zhiding" && (
          <>
            <div className="bg-blue-50 text-blue-700 text-xs p-2 rounded-lg mb-3 flex items-start">
              请依次指定每一爻的阴阳性质。
            </div>

            {!isZhidingComplete && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  请选择 {LINE_NAMES[5 - filledZhidingCount]}
                </label>
                <select
                  value={currentZhidingInput}
                  onChange={(e) => handleZhidingSelect(e.target.value)}
                  className="block w-full h-10 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base sm:text-sm border p-2 cursor-pointer hover:border-indigo-300 transition-colors"
                >
                  <option value="">-- 请选择 --</option>
                  {ZHIDING_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-2 p-2 bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-xs text-gray-500 mb-2">已指定爻象（从初爻到上爻）：</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {[5, 4, 3, 2, 1, 0].map((idx, i) => (
                  <div key={idx} className="flex flex-col items-center">
                    <div className={`w-12 h-9 flex items-center justify-center rounded border font-medium text-xs transition-all ${
                      zhidingInputs[idx] 
                        ? 'bg-white border-indigo-500 text-indigo-600 shadow-sm' 
                        : 'bg-gray-100 border-gray-200 text-gray-300 border-dashed'
                    }`}>
                      {zhidingInputs[idx] ? ZHIDING_MAP[zhidingInputs[idx]] : "?"}
                    </div>
                    <span className="text-[10px] text-gray-300">{LINE_NAMES[idx]}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
        <button
          onClick={resetAll}
          className="px-4 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-colors shadow-sm text-sm"
        >
          重置
        </button>
        <button
          onClick={handleStart}
          disabled={
            !formData.method || 
            (formData.method === "baoshu" && !isBaoshuComplete) ||
            (formData.method === "zhiding" && !isZhidingComplete) ||
            (formData.method === "zhuyao" && !isZhuyaoComplete)
          }
          className={`px-4 py-2 rounded-lg font-bold shadow-md transition-all transform text-sm ${
            !formData.method ||
            (formData.method === "baoshu" && !isBaoshuComplete) ||
            (formData.method === "zhiding" && !isZhidingComplete) ||
            (formData.method === "zhuyao" && !isZhuyaoComplete)
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white hover:shadow-lg hover:-translate-y-0.5"
          }`}
        >
          开始排盘
        </button>
      </div>
      {!formData.method && errors.start && <div className="text-red-600 text-xs mt-2 text-center bg-red-50 p-1.5 rounded border border-red-100">请选择起卦方式</div>}
      {formData.method && errors.start && <div className="text-red-600 text-xs mt-2 text-center bg-red-50 p-1.5 rounded border border-red-100">{errors.start}</div>}
    </section>
  );
};

// --- Main Component ---

const Liuyao = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // Unified formData
  const now = new Date();
  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    shudi: "",
    birth_year: "",
    birth_month: "",
    birth_day: "",
    birth_hour: "",
    type: "", // zhanti_type
    content: "", // zhanti
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    method: "", // Changed from "zhuyao" to ""
  });

  // UI States for input methods
  const [lines, setLines] = useState(DEFAULT_LINES.slice());
  const [baoshuInputs, setBaoshuInputs] = useState(["", "", "", "", "", ""]);
  const [currentBaoshuInput, setCurrentBaoshuInput] = useState(""); // New state for single input
  
  // Zhiding states
  const [zhidingInputs, setZhidingInputs] = useState(["", "", "", "", "", ""]);
  const [currentZhidingInput, setCurrentZhidingInput] = useState("");

  const [errors, setErrors] = useState({});
  const [zhantiOptions, setZhantiOptions] = useState([]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await apiService.getZhantiTypes();
        if (types && types.length > 0) {
          setZhantiOptions(types);
        }
      } catch (error) {
        console.error("Failed to load zhanti types", error);
      }
    };
    fetchTypes();

    const key = searchParams.get("stateKey");
    if (!key) return;
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed) {
        setFormData((prev) => ({
          ...prev,
          name: parsed.mingzhuName || "",
          gender: parsed.mingzhuGender || "",
          shudi: parsed.shudi || "",
          type: parsed.zhanti_type || "",
          content: parsed.zhanti || "",
        }));
      }
    } catch (err) {}
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["year", "month", "day", "hour", "birth_year", "birth_month", "birth_day", "birth_hour"].includes(name)) {
      if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: value }));
        return;
      }
      const num = parseInt(value);
      if (isNaN(num) || num < 0) return;
      if (name === "year" && num > 2100) return;
      if (name === "month" && (num > 12 || num === 0)) return;
      if (name === "day" && (num > 31 || num === 0)) return;
      if (name === "hour" && num > 23) return;
      setFormData((prev) => ({ ...prev, [name]: num }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const resetAll = () => {
    setLines(DEFAULT_LINES.slice());
    setBaoshuInputs(["", "", "", "", "", ""]);
    setCurrentBaoshuInput("");
    setZhidingInputs(["", "", "", "", "", ""]);
    setCurrentZhidingInput("");
    setErrors({});
  };

  const handleMethodChange = (e) => {
    const newMethod = e.target.value;
    setFormData((prev) => ({ ...prev, method: newMethod }));
    resetAll();
  };

  // Handle single input change
  const handleCurrentBaoshuChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 3);
    setCurrentBaoshuInput(val);
  };

  // Confirm single input
  const confirmBaoshuInput = () => {
    if (!/^\d{3}$/.test(currentBaoshuInput)) {
      alert("请输入3位数字");
      return;
    }

    // Find the next empty slot from bottom (index 5) to top (index 0)
    const filledCount = baoshuInputs.filter(v => v !== "").length;
    if (filledCount >= 6) return;

    const targetIndex = 5 - filledCount; // 5, 4, 3, 2, 1, 0
    
    const newInputs = [...baoshuInputs];
    newInputs[targetIndex] = currentBaoshuInput;
    setBaoshuInputs(newInputs);
    setCurrentBaoshuInput(""); // Clear input
  };

  // Confirm zhiding input - Modified to handle selection directly
  const handleZhidingSelect = (val) => {
    if (!val) return;

    const filledCount = zhidingInputs.filter(v => v !== "").length;
    if (filledCount >= 6) return;

    const targetIndex = 5 - filledCount;
    const newInputs = [...zhidingInputs];
    newInputs[targetIndex] = val;
    setZhidingInputs(newInputs);
    setCurrentZhidingInput("");
  };

  const validateBeforeStart = () => {
    const err = {};
    if (!formData.type) err.type = "占类为必填项";
    if (!formData.content || String(formData.content).trim() === "")
      err.content = "请输入求占问题（必填）";

    if (formData.year < 1900 || formData.year > 2100) {
      err.year = "年份应在1900-2100之间";
      err.start = "年份应在1900-2100之间";
    }

    if (formData.method === "baoshu") {
      const filledCount = baoshuInputs.filter(v => v !== "").length;
      if (filledCount < 6) {
        err.start = "请完成所有6爻的报数";
      }
    }

    if (formData.method === "zhiding") {
      const filledCount = zhidingInputs.filter(v => v !== "").length;
      if (filledCount < 6) {
        err.start = "请完成所有6爻的指定";
      }
    }

    if (formData.method === "zhuyao") {
      const missing = Array.from({ length: LINE_NAMES.length }).some((_, i) => {
        const v = lines[i];
        return !v || v === "待生成";
      });
      if (missing) {
        err.start = "请先为六个爻生成数值，所有爻都必须已生成";
      }
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleStart = async () => {
    if (!validateBeforeStart()) return;

    let rawValues = [];
    if (formData.method === "baoshu") {
      rawValues = baoshuInputs.slice();
    } else if (formData.method === "zhiding") {
      rawValues = zhidingInputs.map(v => parseInt(v, 10));
    } else {
      rawValues = lines.slice();
    }

    const divinationNumbers = rawValues.map((v) => {
      const digits = String(v).replace(/\D/g, "");
      if (!digits) return 0;
      let oddCount = 0;
      for (let ch of digits) {
        const d = Number(ch);
        if (!Number.isNaN(d) && d % 2 === 1) oddCount++;
      }
      return oddCount;
    }).reverse();

    const pad = (num, len = 2) => String(num).padStart(len, "0");
    const datetimeStr = `${formData.year}${pad(formData.month)}${pad(
      formData.day
    )}${pad(formData.hour)}0000`;

    try {
      const requestData = {
        ...formData,
        mingzhuName: formData.name,
        mingzhuGender: formData.gender,
        zhanti_type: formData.type,
        zhanti: formData.content,
        datetimeStr,
        divinationNumbers,
      };

      const response = await axios.post("/api/liuyao/calculate", requestData);
      if (response.data.success) {
        const result = response.data.data;
        navigate(`/liuyao/LiuyaoDetail`, {
          state: {
            form: requestData,
            result: result,
            requestPayload: requestData,
            liuyaoResult: result,
          },
        });
      } else {
        alert("排盘失败: " + response.data.error);
      }
    } catch (err) {
      console.error("排盘计算失败:", err);
      alert("排盘计算失败，请检查输入或网络。");
    }
  };

  const resetToCurrentTime = () => {
    const n = new Date();
    setFormData((prev) => ({
      ...prev,
      year: n.getFullYear(),
      month: n.getMonth() + 1,
      day: n.getDate(),
      hour: n.getHours(),
    }));
  };

  return (
    <div className="bg-gray-50 py-8 overflow-x-hidden">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
            六爻排盘
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
           诚心静默，排除杂念 
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Left Panel: Basic Info */}
          <div className="h-full min-w-0">
            <BasicInfoForm
              formData={formData}
              handleChange={handleChange}
              zhantiOptions={zhantiOptions}
              errors={errors}
              resetToCurrentTime={resetToCurrentTime}
            />
          </div>

          {/* Right Panel: Divination Method */}
          <div className="h-full min-w-0">
            <DivinationMethod
              formData={formData}
              handleMethodChange={handleMethodChange}
              lines={lines}
              setLines={setLines}
              baoshuInputs={baoshuInputs}
              currentBaoshuInput={currentBaoshuInput}
              handleCurrentBaoshuChange={handleCurrentBaoshuChange}
              confirmBaoshuInput={confirmBaoshuInput}
              zhidingInputs={zhidingInputs}
              currentZhidingInput={currentZhidingInput}
              handleZhidingSelect={handleZhidingSelect}
              handleStart={handleStart}
              resetAll={resetAll}
              errors={errors}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Liuyao;